import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Button from '../Components/Buttons';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { useOrganizationApi } from '../Services/OrganizationApiProvider';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';

function Organizations({ canEdit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();

  const { getOrganizations } = useOrganizationApi();

  const [alertShown, setAlertShown] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [organizations, setOrganizations] = React.useState([]);
  const [totalOrganizations, setTotalOrganizations] = React.useState(0);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const tableTitle = 'Organizations';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const columns = [
    { name: 'Title', selector: (row) => row.title, sortable: false },
    {
      name: 'Status',
      selector: (row) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: false,
    },
  ];

  React.useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${row.id}`);
  };

  const fetchOrganizations = async (skip, limit, isActive = selectedStatus) => {
    try {
      setLoading(true);
      const response = await getOrganizations(skip, limit, isActive);
      setOrganizations(response.items);
      setTotalOrganizations(response.totalCount);
    } catch (error) {
      setError(error.message || 'Failed to fetch organizations!');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchOrganizations(persistedSkip, persistedPageSize);
  }, []);

  const handleStatusChange = async (skip, limit, status) => {
    setSelectedStatus(status);
    await fetchOrganizations(skip, limit, status);
  };

  return (
    <div className="container">
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={organizations}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={persistedPageSize}
                paginationTotalRows={totalOrganizations}
                dense={false}
                noTableHead={false}
                disabled={false}
                onRowClicked={handleRowClicked}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                rowKeyField="key"
                onChangePage={fetchOrganizations}
                getRowUrl={(row) => `/organizations/${row.id}`}
                SearchField
                onChangeFilter={handleStatusChange}
                typeSearch="multiselect"
                searchOptions={[
                  { label: 'Active', value: true },
                  { label: 'InActive', value: false },
                ]}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() => navigate('/new-organization')}
                >
                  Create New Organization
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function OrganizationsWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <Organizations canEdit={canEdit} />
    </ConfirmationProvider>
  );
}

export default OrganizationsWrapper;
